import React, { useState, useEffect, useCallback, useContext } from 'react';
import { getGlobalState, getUserTeams, getWeeklyResults, markWeeklyResultsViewed } from '../DatabaseAPI';
import AuthContext from '../auth/AuthContext';
import { Button, Select, Modal } from '../components/ui';
import WeeklyResultsPopup from '../components/results/WeeklyResultsPopup';
import TeamEditModal from '../components/league/TeamEditModal';
import SurveyModal from '../components/survey/SurveyModal';
import TutorialPopup from '../components/tutorial/TutorialPopup';
import './MyTeamsPage.css';

/**
 * Crest used as the week selector's left icon.
 *
 * Inline SVG rather than a file under /art: the header pill needs a mark that
 * recolours with the surrounding text (`currentColor`) and stays crisp at 14–18px,
 * and no existing asset does that — `/art/Logo.png` is a wide 1024px banner.
 */
const WeekCrestIcon = () => (
    <svg
        className="week-crest-icon"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
    >
        <path
            d="M12 2.5 4.5 5.4v6.1c0 4.7 3.1 8.3 7.5 10 4.4-1.7 7.5-5.3 7.5-10V5.4L12 2.5Z"
            fill="currentColor"
            fillOpacity="0.18"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
        />
        <path
            d="M8.6 8.6 12 15.2l3.4-6.6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// `onBack` is intentionally NOT destructured. This page IS the Leagues hub, and
// its header no longer carries a BACK control: the approved header is
// week selector + Shop + avatar on both desktop and mobile. AppRouter may still
// pass the prop; ignoring it here keeps that harmless rather than resurrecting a
// button the design removed.
function MyTeamsPage({ user, onNewTeam, onTeamClick, setPage, shopFreePullAvailable }) {
    const { mythrilBalance } = useContext(AuthContext);

    const [teams, setTeams] = useState([]);
    // `null` until GlobalState answers. It must NOT start at a made-up number:
    // the week is a SELECTOR for which weekly league's teams exist, so a
    // placeholder week fetches a different league than the user is in and
    // renders "No teams yet" over a team that exists. See fetchCurrentWeek().
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [availableYears] = useState([2024, 2025]);
    const [globalState, setGlobalState] = useState(null);

    // Weekly Results Popup State
    const [showWeeklyResults, setShowWeeklyResults] = useState(false);
    const [weeklyResultsData, setWeeklyResultsData] = useState(null);

    // Team Edit Modal State
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null);

    // Button hover states for image swapping
    const [homeButtonHovered, setHomeButtonHovered] = useState(false);
    const [joinButtonHovered, setJoinButtonHovered] = useState(false);
    const [editButtonHovered, setEditButtonHovered] = useState({});
    
    // MYTHRIL popup state
    const [showMythrilPopup, setShowMythrilPopup] = useState(false);

    // Survey Modal State
    const [showSurveyModal, setShowSurveyModal] = useState(false);
    
    // Sequential Tutorial State
    const [showTutorial, setShowTutorial] = useState(false);
    const [tutorialStep, setTutorialStep] = useState(0);
    const [tutorialSequence] = useState([
        {
            page_identifier: 'myteams_welcome',
            title: 'Welcome to Mythic Football',
            content_html: '<p>Your Mythic Battle Awaits!</p>',
            highlightElementId: null
        },
        {
            page_identifier: 'myteams_join_league',
            title: 'Build Your Super Team',
            content_html: '<p>It\'s fast and easy! Just hit the "JOIN LEAGUE" button to get started.</p>',
            highlightElementId: 'join-league-button'
        },
        {
            page_identifier: 'myteams_week_selector',
            title: 'Team History',
            content_html: '<p>Use the <strong>WEEK</strong> selector to look back at previous weeks\' results.</p>',
            highlightElementId: 'week-dropdown'
        },
        {
            page_identifier: 'myteams_mythril',
            title: 'Mythril Store',
            content_html: '<p>Unlock new player cards and team themes in the Mythril Store.</p>',
            highlightElementId: 'mythril-button'
        },
        {
            page_identifier: 'myteams_leaderboard',
            title: 'Leaderboard',
            content_html: '<p>"LEADERBOARD" shows you how you stack up against the rest.</p>',
            highlightElementId: 'leaderboard-button'
        }
    ]);

    // THE SERVER OWNS THE WEEK. THE BROWSER CLOCK DOES NOT.
    //
    // `week` is not a display preference — it SELECTS which weekly league's
    // teams `GET /api/v2/auth/teams/` returns, because a weekly league IS a
    // single week (`League.current_week`) and next week is a different league
    // row. Send the wrong week and the endpoint returns 200 with an empty list,
    // which this page renders as "No teams yet" on top of a team that exists
    // and is correctly owned. There is no error to see: the request succeeded.
    //
    // This function used to derive the week from `new Date()` against a
    // hardcoded Sept 1 season start, and before that from a stale localStorage
    // pin, with a hardcoded `4` as the catch-all. All three are guesses about
    // server state made on the client. Once the real GlobalState week drifted
    // away from the guess (e.g. GlobalState sat at week 1 while the calendar
    // said week 2), every user's teams disappeared from this page while their
    // profile and Mythril balance kept loading fine — because those endpoints
    // take no week parameter.
    //
    // So the week now comes from ONE source: `current_week` on
    // /api/v2/global-state/, read through getGlobalState() and applied in
    // fetchGlobalState(). A saved localStorage value is only honoured when it
    // names a week the user could have deliberately chosen with the selector,
    // and is never used to bootstrap the initial load.
    //
    // THE REQUEST GOES THROUGH THE SHARED API CLIENT, NOT A BARE `fetch`.
    // This function previously called `fetch('/api/v2/global-state/')` with a
    // path relative to the PAGE origin. In dev the page is served by the CRA
    // dev server on :3000 while Django is on :8000, and there is no `proxy`
    // entry in frontend/package.json and no setupProxy.js — so the dev server
    // answered that path with the SPA's own index.html, `200 text/html`. The
    // guard below then correctly refused to guess a week and the page rendered
    // nothing. The route itself was never wrong: `/api/v2/global-state/`
    // resolves to GlobalStateView. Only the ORIGIN was, which is exactly what
    // `API_BASE` inside getGlobalState() exists to resolve. Every other call on
    // this page (getUserTeams, getWeeklyResults) already went through it.
    const fetchGlobalState = useCallback(async () => {
        // getGlobalState() throws on a non-OK status, a non-JSON body, or a
        // missing/non-integer current_year/current_week. NO HARDCODED FALLBACK:
        // a guessed year/week silently selects the wrong weekly league and
        // empties the team list, so a missing value must be a hard failure the
        // user can see rather than an invisible wrong answer. The old
        // `{ current_year: 2025, current_week: 1 }` fallback was itself a latent
        // version of this bug, waiting for the real week to move.
        const data = await getGlobalState();

        setGlobalState(data);

        if (selectedYear === null) {
            setSelectedYear(data.current_year);
        }

        // Adopt the server's week unless the user has explicitly picked another
        // one with the week selector during this browser's history. The stored
        // value is validated against the 1..22 range the selector offers; a
        // value outside it is stale/corrupt and is discarded rather than used.
        if (selectedWeek === null) {
            const savedWeek = parseInt(localStorage.getItem('myTeamsPage_selectedWeek'), 10);
            const hasUsableSavedWeek = Number.isInteger(savedWeek) && savedWeek >= 1 && savedWeek <= 22;
            setSelectedWeek(hasUsableSavedWeek ? savedWeek : data.current_week);
        }

        return data;
    }, [selectedYear, selectedWeek]);

    const fetchUserTeams = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            let state = globalState;
            if (!state) {
                state = await fetchGlobalState();
            }

            const year = selectedYear || state?.current_year;
            const week = selectedWeek;

            // Both are SELECTORS, not decorations — see fetchCurrentWeek's note.
            // Omitting them makes getUserTeams() drop the query params and the
            // backend answer for GlobalState's week, which may not be the week
            // this render is showing; a hardcoded `2025` would pin the list to a
            // year the user may not be playing. Either way the list silently
            // describes a different contest than the page claims, so refuse.
            if (!Number.isInteger(year) || !Number.isInteger(week)) {
                throw new Error(
                    '❌ MyTeamsPage: refusing to request teams without a resolved year/week — ' +
                    `year=${JSON.stringify(year)} week=${JSON.stringify(week)}. These select which ` +
                    'weekly league the team list comes from; a guessed value returns an empty ' +
                    'list that is indistinguishable from owning no teams.'
                );
            }

            const response = await getUserTeams(week, year);

            if (response && response.teams) {
                // GET /api/v2/auth/teams/ RETURNS BOTH MODES, AND `mode` MUST
                // SURVIVE THIS MAPPING.
                //
                // This mapper rebuilds every API row as a NEW object literal, so
                // any field not copied here is destroyed — the row that reaches
                // AppRouter is only ever what this function returns.
                //
                // It used to claim the endpoint was "WEEKLY (daily) teams only"
                // with "no way to identify a season team", and therefore dropped
                // the mode. That stopped being true when season landed:
                // UserTeamsView now unions `league__mode='SEASON'` rows into the
                // queryset and emits `mode` for every row, and UserTeamsSerializer
                // declares it as a REQUIRED CharField. The comment stayed; the
                // code stayed; season teams silently arrived here with their mode
                // stripped.
                //
                // The consequence was not cosmetic. `mode` is the ONLY client-side
                // signal that chooses between SeasonTeamPage and the daily
                // TeamPage, so every season team clicked from this list reached
                // AppRouter.handleTeamClick() as mode=undefined and threw
                // "INVALID TEAM MODE" — the guard doing its job on data this
                // function broke. Anything added to this object must be copied,
                // not assumed.
                const teamsWithWeeklyData = response.teams.map((team) => {
                    return {
                    id: team.team_id,
                    name: team.team_name,
                    display_name: team.display_name || team.team_name,
                    leagueId: team.league_id,
                    leagueName: team.league_name,
                    leagueType: team.league_type,
                    // 'WEEKLY' | 'SEASON'. Copied verbatim and NEVER defaulted:
                    // a wrong mode opens a roster screen whose endpoints reject
                    // the other mode, so guessing produces backend errors rather
                    // than a degraded view. Validated below before it is stored.
                    mode: team.mode,
                    leagueStatus: team.league_status,
                    wins: team.wins,
                    losses: team.losses,
                    ties: team.ties,
                    weeklyScore: team.weekly_score,
                    ranking: team.ranking,
                    totalTeams: team.total_teams,
                    week: selectedWeek,
                    year: selectedYear,
                    canChangeRoster: team.can_change_roster,
                    automationEnabled: team.automation_enabled,
                    seasonType: team.season_type
                    };
                });

                // FAIL HERE, NOT ON CLICK.
                //
                // AppRouter.handleTeamClick() already rejects a bad mode, but it
                // only runs when the user clicks a card — so a broken response
                // renders a normal-looking list that detonates on interaction,
                // and the error points at the endpoint rather than at whoever
                // dropped the field. Validating the whole payload at the point of
                // ingestion turns that into one honest load failure with every
                // offending row named.
                const invalidModeTeams = teamsWithWeeklyData.filter(
                    (team) => team.mode !== 'WEEKLY' && team.mode !== 'SEASON'
                );
                if (invalidModeTeams.length > 0) {
                    throw new Error(
                        '❌ INVALID TEAM MODE from GET /api/v2/auth/teams/: ' +
                        invalidModeTeams
                            .map((team) => `team ${team.id} ("${team.display_name}") has mode=${JSON.stringify(team.mode)}`)
                            .join('; ') +
                        ". Expected 'WEEKLY' or 'SEASON' on every row — `mode` is a " +
                        'REQUIRED field on UserTeamsSerializer and is the only signal ' +
                        'that chooses between the season and daily roster screens. ' +
                        'Refusing to render team cards that cannot be routed.'
                    );
                }

                setTeams(teamsWithWeeklyData);
            } else {
                setTeams([]);
            }
        } catch (err) {
            // Surface the REAL message, not a generic one. A contract failure
            // (a row with no routable `mode`) and a network blip need different
            // responses from whoever is looking at the screen, and "Please try
            // again" is actively misleading for the former — retrying a
            // malformed payload just reproduces it.
            console.error('Error fetching user teams:', err);
            setError(err.message || 'Failed to load teams. Please try again.');
            setTeams([]);
        } finally {
            setLoading(false);
        }
    }, [selectedWeek, selectedYear, globalState, fetchGlobalState]);

    useEffect(() => {
        // fetchGlobalState() no longer swallows failures into a hardcoded
        // year/week, so a failure here must land somewhere the user can see it
        // instead of becoming an unhandled promise rejection that leaves the
        // page stuck on "Loading your teams...".
        fetchGlobalState().catch((err) => {
            console.error('MyTeamsPage: could not resolve global state:', err);
            setError(err.message || 'Failed to load the current week.');
            setLoading(false);
        });

        // Check if tutorial should be shown
        const tutorialViewed = localStorage.getItem('tutorial_viewed_myteams_welcome');
        const tutorialsDisabled = localStorage.getItem('tutorials_disabled');
        if (!tutorialViewed && !tutorialsDisabled) {
            setShowTutorial(true);
        }
        
        // Show survey modal on page load (temporary for testing)
        setShowSurveyModal(true);
    }, [fetchGlobalState]);

    useEffect(() => {
        // `selectedWeek` is null until GlobalState resolves. Asking for
        // "week null" results would either 400 or answer for some other week,
        // so wait for the real one rather than popping a results modal built
        // from the wrong week's scores.
        if (!loading && !showWeeklyResults && Number.isInteger(selectedWeek)) {
            checkForWeeklyResults();
        }
    }, [loading, teams, selectedWeek]);

    const checkForWeeklyResults = async () => {
        try {
            console.log(`MyTeamsPage: Checking for weekly results for week ${selectedWeek}...`);
            const response = await getWeeklyResults(selectedWeek);

            if (response.needs_popup && response.results.length > 0) {
                console.log('MyTeamsPage: Weekly results received:', response.results);
                
                // Validate and filter results to ensure each league has valid team data
                const validResults = response.results.filter(league => {
                    // Check if league has teams array and at least one team
                    if (!league.teams || !Array.isArray(league.teams) || league.teams.length === 0) {
                        console.warn('MyTeamsPage: Skipping league without valid teams data:', {
                            league_id: league.league_id,
                            league_name: league.league_name,
                            has_teams: !!league.teams,
                            teams_length: league.teams?.length
                        });
                        return false;
                    }
                    
                    // Check if the first team has required data
                    const team = league.teams[0];
                    if (!team || !team.team_id) {
                        console.warn('MyTeamsPage: Skipping league with invalid team data:', {
                            league_id: league.league_id,
                            league_name: league.league_name,
                            team: team
                        });
                        return false;
                    }
                    
                    return true;
                });
                
                if (validResults.length > 0) {
                    console.log('MyTeamsPage: Showing popup with valid results:', validResults);
                    setWeeklyResultsData(validResults);
                    setShowWeeklyResults(true);
                } else {
                    console.warn('MyTeamsPage: No valid weekly results to display after filtering');
                }
            } else {
                console.log('MyTeamsPage: No weekly results needed');
            }
        } catch (error) {
            console.error('MyTeamsPage: Error checking weekly results:', error);
            
            // If it's an authentication error, the user might need to log in again
            if (error.message && error.message.includes('Authentication failed')) {
                console.error('MyTeamsPage: Authentication failed - user may need to log in again');
            }
        }
    };

    const handleMarkWeeklyResultsViewed = async (leagueId, seasonYear, week) => {
        try {
            await markWeeklyResultsViewed(leagueId, seasonYear, week);
            console.log(`MyTeamsPage: Marked results viewed for league ${leagueId}`);
        } catch (error) {
            console.error('MyTeamsPage: Error marking results viewed:', error);
        }
    };

    const handleCloseWeeklyResults = async () => {
        console.log(`MyTeamsPage: Closing weekly results popup - marking all leagues as viewed`);
        
        // Mark ALL leagues in the results as viewed on the backend
        if (weeklyResultsData && weeklyResultsData.length > 0) {
            for (const league of weeklyResultsData) {
                try {
                    await handleMarkWeeklyResultsViewed(
                        league.league_id,
                        league.season_year,
                        league.week
                    );
                } catch (error) {
                    console.error(`Failed to mark league ${league.league_id} as viewed:`, error);
                }
            }
        }
        
        setShowWeeklyResults(false);
        setWeeklyResultsData(null);
    };

    useEffect(() => {
        if (selectedWeek && globalState) {
            fetchUserTeams();
        }
    }, [selectedWeek, selectedYear, globalState, fetchUserTeams]);

    const handleWeekChange = (event) => {
        const newWeek = parseInt(event.target.value);
        setSelectedWeek(newWeek);
        // Save the selected week to localStorage for persistence
        localStorage.setItem('myTeamsPage_selectedWeek', newWeek.toString());
    };

    const handleYearChange = (event) => {
        setSelectedYear(parseInt(event.target.value));
    };

    const handleNewTeam = () => {
        // onNewTeam drives the Join League flow (AppRouter -> LeagueSignUpPage).
        // A missing handler means the button is dead — fail loudly, never silently.
        if (!onNewTeam) {
            throw new Error('MyTeamsPage: onNewTeam prop is required but not provided — JOIN LEAGUE cannot navigate to the league signup flow');
        }
        onNewTeam();
    };

    const getRankBadge = (rank) => {
        if (!rank || rank === null) return { text: '--', color: '#666', icon: '' };
        if (rank === 1) return { text: '1ST', color: '#FFD700', icon: '🏆' };
        if (rank === 2) return { text: '2ND', color: '#C0C0C0', icon: '🥈' };
        if (rank === 3) return { text: '3RD', color: '#C0C0C0', icon: '🥉' };
        if (rank <= 10) return { text: `${rank}TH`, color: '#CD7F32', icon: '' };
        return { text: `${rank}TH`, color: '#666', icon: '' };
    };

    // TWO DIFFERENT STATUS VOCABULARIES SHARE ONE FIELD.
    //
    // `league_status` is NOT one enum. UserTeamsView fills it from a different
    // source per mode: weekly rows get `league.status`
    // (PRE_GAMES/GAMES_ACTIVE/CRUNCHING/COMPLETED) while season rows get
    // `season_config.status` (FORMING/DRAFTING/REGULAR_SEASON/PLAYOFFS/
    // COMPLETED). The two overlap only on COMPLETED and mean different things
    // everywhere else.
    //
    // So the mode has to select the vocabulary before the status is read. A
    // single flat switch cannot do this: run a season row through the weekly
    // cases and every value except COMPLETED falls to `default`, labelling an
    // actively drafting campaign "Unknown" — the exact wrong-screen-class
    // failure the mode field exists to prevent, in miniature.
    const getStateIndicator = (team) => {
        const state = team.leagueStatus;

        if (team.mode === 'SEASON') {
            switch (state) {
                case 'FORMING':
                    return { color: 'var(--color-info)', text: 'FORMING', description: 'Waiting for managers to join' };
                case 'DRAFTING':
                    return { color: 'var(--color-warning)', text: 'DRAFT', description: 'Draft in progress' };
                case 'REGULAR_SEASON':
                    return { color: 'var(--color-success)', text: 'SEASON', description: 'Regular season underway' };
                case 'PLAYOFFS':
                    return { color: 'var(--color-accent-gold)', text: 'PLAYOFF', description: 'Playoffs underway' };
                case 'COMPLETED':
                    return { color: 'var(--color-text-muted)', text: 'DONE', description: 'Campaign complete' };
                default:
                    // Never silently label an unknown lifecycle as generic. A
                    // status this client does not recognise means the season
                    // lifecycle grew and this list was not updated with it.
                    throw new Error(
                        `❌ UNKNOWN SEASON LEAGUE STATUS in MyTeamsPage: ${JSON.stringify(state)} ` +
                        `for team ${team.id} ("${team.display_name}"). Expected one of ` +
                        'FORMING, DRAFTING, REGULAR_SEASON, PLAYOFFS, COMPLETED ' +
                        '(SeasonLeagueConfig.STATUS_CHOICES).'
                    );
            }
        }

        switch(state) {
            case 'PRE_GAMES':
                return { color: '#e53e3e', text: 'EQUIP', description: 'Equipment Phase' };
            case 'GAMES_ACTIVE':
                return { color: '#e53e3e', text: 'ACTIVE', description: 'Games Active' };
            case 'CRUNCHING':
                return { color: '#e53e3e', text: 'CRUNCH', description: 'Processing' };
            case 'COMPLETED':
                return { color: '#e53e3e', text: 'CLOSED', description: 'League Closed' };
            default:
                return { color: '#666', text: '--', description: 'Unknown' };
        }
    };

    const handleTeamClick = (team) => {
        console.log('Navigate to team management for team:', team.id, team.name);
        sessionStorage.setItem('previousLocation', window.location.pathname);

        if (onTeamClick) {
            onTeamClick(team);
        }
    };

    const handleEditTeam = (team, event) => {
        event.stopPropagation();
        console.log('Edit team clicked for:', team);

        const freshTeamData = teams.find(t => t.id === team.id) || team;
        setSelectedTeamForEdit(freshTeamData);
        setShowEditModal(true);
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
        setSelectedTeamForEdit(null);
    };

    const handleEditModalSave = (updatedTeam) => {
        console.log('Team updated:', updatedTeam);
        setTeams(prevTeams =>
            prevTeams.map(team =>
                team.id === updatedTeam.id
                    ? { ...team, ...updatedTeam }
                    : team
            )
        );
        setShowEditModal(false);
        setSelectedTeamForEdit(null);
    };

    const handleMythrilClick = () => {
        if (setPage) { setPage('storePage'); } // Navigate to the Ye Olde Store page
    };

    const handleCloseMythrilPopup = () => {
        setShowMythrilPopup(false);
    };

    const handleLeaderboardClick = () => {
        if (setPage) {
            setPage('LEADERBOARD');
        }
    };

    const handleCloseSurveyModal = () => {
        setShowSurveyModal(false);
    };

    const handleSurveySubmit = () => {
        console.log('Survey submitted successfully');
        // Balance is now managed globally via AuthContext — no local refresh needed.
    };
    
    const handleCloseTutorial = () => {
        setShowTutorial(false);
        setTutorialStep(0);
    };
    
    const handleNextTutorialStep = () => {
        setTutorialStep(prev => prev + 1);
    };
    
    const handleMarkTutorialViewed = async (pageIdentifier, neverShowAgain) => {
        console.log('Tutorial marked as viewed:', pageIdentifier, 'Never show again:', neverShowAgain);
        if (neverShowAgain) {
            localStorage.setItem('tutorials_disabled', 'true');
        }
        localStorage.setItem(`tutorial_viewed_${pageIdentifier}`, 'true');
    };

    const renderTeamRow = (team, index) => {
        const rankInfo = getRankBadge(team.ranking);
        const stateInfo = getStateIndicator(team);
        const isEditHovered = editButtonHovered[team.id] || false;

        return (
            <div
                key={team.id}
                className={`team-row ${index % 2 === 0 ? 'even' : 'odd'}`}
                onClick={() => handleTeamClick(team)}
                title={stateInfo.description}
            >
                {/* Rank Section - Fixed Size Badge */}
                <div className="rank-section">
                    <div
                        className="rank-badge-fixed"
                        style={{
                            borderColor: rankInfo.color,
                            color: rankInfo.color
                        }}
                    >
                        <div className="rank-text">{rankInfo.text}</div>
                        {rankInfo.icon && <div className="rank-icon">{rankInfo.icon}</div>}
                    </div>
                </div>

                {/* Team Info Section */}
                <div className="team-info-section">
                    <div className="team-name">{team.display_name}</div>
                    <div className="owner-name">({team.name})</div>
                    <div className="league-name">
                        {team.leagueName || 'League'}
                        {/* SEASON ROWS NEED TO LOOK DIFFERENT, because clicking
                            them goes somewhere different. This list now mixes
                            both league types, and until now they were visually
                            identical — same row, same score column, same year
                            badge — so the only way to discover that a row led to
                            the campaign screen instead of the daily one was to
                            click it. The lifecycle word is shown alongside
                            because a season row's status vocabulary (FORMING /
                            DRAFT / SEASON …) is otherwise invisible: stateInfo
                            was previously only ever used as a hover title, which
                            does not exist on touch. */}
                        {team.mode === 'SEASON' && (
                            <span
                                className="season-badge"
                                style={{ color: stateInfo.color, borderColor: stateInfo.color }}
                            >
                                CAMPAIGN · {stateInfo.text}
                            </span>
                        )}
                        {/* The year selector governs WEEKLY rows only — season
                            teams are returned regardless of it — so this
                            "different year" marker is meaningless on a season
                            row and would read as an anomaly on every one. */}
                        {team.mode !== 'SEASON' && team.year !== selectedYear && (
                            <span className="year-badge">({team.year})</span>
                        )}
                    </div>
                </div>

                {/* Right Section: Large Score Only */}
                <div className="right-section">
                    <div className="score-display-large">
                        {/* Season rows carry no weekly_score by contract
                            (UserTeamsView sets it to None), so this is '--' for
                            them — an honest "not applicable here" rather than a
                            zero that would read as a real result. */}
                        {team.weeklyScore || team.weeklyScore === 0
                            ? typeof team.weeklyScore === 'number'
                                ? team.weeklyScore.toFixed(2)
                                : team.weeklyScore
                            : '--'}
                    </div>
                </div>
            </div>
        );
    };

    // Week options for the header selector.
    //
    // Values are STRINGS because `Select` declares `value`/`option.value` as
    // strings; the previous numeric version tripped propTypes on every render.
    // `handleWeekChange` parses back to an integer before anything stores it, so
    // the week that reaches getUserTeams() is still a number.
    const weekOptions = [
        // Regular Season Weeks 1-18
        ...Array.from({ length: 18 }, (_, i) => ({
            value: String(i + 1),
            label: `WEEK ${i + 1}`
        })),
        // Playoff Weeks 19-22
        ...Array.from({ length: 4 }, (_, i) => ({
            value: String(i + 19),
            label: `PLAYOFFS WEEK ${i + 1}`
        }))
    ];

    // `selectedWeek` is null until GlobalState answers (see fetchGlobalState).
    // The control still renders so the header does not reflow when the week
    // lands, but it is disabled and shows a placeholder — offering a pickable
    // week before the server's is known invites the user to "choose" a week
    // that is about to be overwritten.
    //
    // The placeholder is prepended as a real option rather than passed via the
    // Select's `placeholder` prop: that prop also sets `defaultValue=""` on an
    // element this page controls with `value`, which is React's
    // controlled/uncontrolled warning.
    const weekResolved = Number.isInteger(selectedWeek);
    const weekSelectOptions = weekResolved
        ? weekOptions
        : [{ value: '', label: 'WEEK —', disabled: true }, ...weekOptions];

    return (
        <div className="my-teams-page">
            {/* New Header Design */}
            <div className="page-header">
                {/* WEEK SELECTOR — this is how previous weeks' results are reached.
                    It is not decoration: `week` selects which weekly league's teams
                    GET /api/v2/auth/teams/ returns, so removing it removed the only
                    way to look at a finished week. Centered on desktop, first in the
                    row on mobile (see MyTeamsPage.css). */}
                <div className="header-week-group">
                    <Select
                        id="week-dropdown"
                        className="header-week-select"
                        size="sm"
                        value={weekResolved ? String(selectedWeek) : ''}
                        onChange={handleWeekChange}
                        options={weekSelectOptions}
                        disabled={!weekResolved}
                        aria-label="Select week"
                        iconLeft={<WeekCrestIcon />}
                    />
                </div>

                {/* Right Side Group: MYTHRIL + Avatar */}
                <div className="header-right-group">
                    {/* Currency Button (💎 MYTHRIL) — navigates to Shop (storePage).
                        Notification dot appears when shopFreePullAvailable is true
                        (free daily pull or punch-card credit available). */}
                    <button
                        id="mythril-button"
                        className="header-currency-button"
                        onClick={handleMythrilClick}
                        style={{ position: 'relative' }}
                    >
                        <img src="/art/diamond_400_white_bg.svg" alt="Diamond" className="diamond-icon" />
                        <span className="currency-label">Shop — {mythrilBalance}</span>
                        {shopFreePullAvailable && (
                            <span
                                className="shop-nav-dot"
                                aria-label="Free pull available"
                                title="Free pull available in Shop!"
                            />
                        )}
                    </button>

                    {/* User Avatar */}
                    <img
                        src="/art/UserIcon.png"
                        alt="User"
                        className="header-user-avatar"
                        onClick={() => {
                            if (!setPage) {
                                throw new Error('MyTeamsPage: setPage prop is required but not provided for account navigation');
                            }
                            setPage('accountPage');
                        }}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
            </div>

            {/* Team List Section */}
            <div className="team-list-section">
                {error && (
                    <div className="error-message">{error}</div>
                )}

                {loading ? (
                    <div className="loading-message">
                        <div className="loading-icon">⏳</div>
                        Loading your teams...
                    </div>
                ) : teams.length === 0 ? (
                    <div className="no-teams-message">
                        <h3>No teams yet</h3>
                        <p>Hit <strong>JOIN LEAGUE</strong> below to build your first super team.</p>
                    </div>
                ) : (
                    <div className="teams-container">
                        {teams.map(renderTeamRow)}
                    </div>
                )}
            </div>

            {/* Bottom Action Bar — JOIN LEAGUE is always available, regardless of
                whether the user already has teams. It doubles as the tutorial
                target for the `join-league-button` highlight step. */}
            <div className="bottom-leaderboard-container">
                <Button
                    id="join-league-button"
                    className="join-league-button"
                    onClick={handleNewTeam}
                    variant="primary"
                >
                    JOIN LEAGUE
                </Button>
                <Button
                    id="leaderboard-button"
                    className="leaderboard-button"
                    onClick={handleLeaderboardClick}
                    variant="secondary"
                >
                    LEADERBOARD
                </Button>
            </div>

            {/* Footer Section — LOG OUT now lives on the Account page (Settings tab) */}
            <div className="footer-section">
                <a
                    href="https://discord.gg/xuzk5MwpEb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="discord-link-button"
                >
                    Join our discord!
                </a>
            </div>

            {/* MYTHRIL Popup */}
            <Modal
                isOpen={showMythrilPopup}
                onClose={handleCloseMythrilPopup}
                title="MYTHRIL"
                size="sm"
            >
                <div className="mythril-icon">🪙</div>
                <p style={{ textAlign: 'center', marginTop: 'var(--space-4)' }}>
                    Mythic Store under construction...
                </p>
            </Modal>

            {/* Weekly Results Popup */}
            {showWeeklyResults && weeklyResultsData && (
                <WeeklyResultsPopup
                    resultsData={weeklyResultsData}
                    onClose={handleCloseWeeklyResults}
                    onMarkViewed={handleMarkWeeklyResultsViewed}
                    onNavigateToStandings={(leagueId) => {
                        console.log('MyTeamsPage: Navigating to standings for league:', leagueId);
                        // setPage prop doesn't handle standingsPage, so we need to use AppRouter's pattern
                        // The sessionStorage is already set by WeeklyResultsPopup's handleLeagueClick
                        // We just need to trigger the navigation by calling setPage with a custom handler
                        if (setPage) {
                            // Use the setPage callback to navigate to standings
                            setPage('standingsPage');
                        }
                    }}
                />
            )}

            {/* Team Edit Modal */}
            {showEditModal && selectedTeamForEdit && (
                <TeamEditModal
                    isOpen={showEditModal}
                    onClose={handleEditModalClose}
                    onSave={handleEditModalSave}
                    team={selectedTeamForEdit}
                />
            )}

            {/* Survey Modal */}
            {showSurveyModal && (
                <SurveyModal
                    onClose={handleCloseSurveyModal}
                    onSubmit={handleSurveySubmit}
                />
            )}
            
            {/* Sequential Tutorial */}
            {showTutorial && (
                <TutorialPopup
                    tutorials={tutorialSequence}
                    currentStep={tutorialStep}
                    onClose={handleCloseTutorial}
                    onNext={handleNextTutorialStep}
                    onMarkViewed={handleMarkTutorialViewed}
                />
            )}
        </div>
    );
}

export default MyTeamsPage;
